/**
 * ATL-928 / ATL-921 — the money one cart line is worth, in a way that does not change at the read
 * cutover.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS EXISTS FOR
 *
 * `aggregateClientTopProducts` (Client Detail → top products, rendered as `saleAmount`) used to do:
 *
 *     const price = cartItem.price || cartItem.finalItemPrice || 0
 *     products[productId].saleAmount += price
 *
 * `saleAmount` is a LINE TOTAL accumulator. `price` is a UNIT price — and it exists ONLY on the
 * Supabase read path (`transformSale` derives it; a Firestore cart item has no `price` key at all,
 * measured 0 of 481 sampled items across BENNALI, ЛЮБИ and TESLA). So the same shared function
 * silently changed meaning at the flip:
 *
 *   • reading Firestore  → falls through to `finalItemPrice`, the charged LINE TOTAL. Correct.
 *   • reading Supabase   → adds `price`, ONE UNIT at the LIST price (ATL-921). Wrong twice over.
 *
 * Beauty Expert sale `TL1DE0Y62hvBCv4fBqTU` (qty 1): 8,300 instead of the 7,470 actually charged.
 * On any line with quantity > 1 the whole line collapses to a single unit as well.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY `finalItemPrice` IS THE LINE TOTAL AND NOT A UNIT PRICE
 *
 * Established from its existing uses rather than assumed — every one of them pairs it against a
 * quantity-multiplied cost:
 *
 *   screens/Stats/statHelpers.js   margin = finalItemPrice − purchasePrice * quantity
 *   utils/validationFunctions.js   saleAmount += finalItemPrice, purchaseAmount += qty * purchase
 *   global/wipon.js                discount = basePrice * quantity − finalItemPrice
 *   backend migration.utils.ts     price = finalItemPrice / quantity   (to GET a unit price)
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS INDEPENDENT OF HOW ATL-921 IS DECIDED
 *
 * ATL-921 is the open question of which derivation `cart_items[].price` should carry — the list
 * price the live dual-write stores, or the charged price the migration stores. This function does
 * not wait for that answer: it prefers the field whose meaning both writers agree on, and reaches
 * for `price` only when there is no `finalItemPrice` at all. Whichever way ATL-921 lands, the
 * number on Client Detail does not move at the flip.
 */

/** The subset of a cart line this rule reads. Loose on purpose — both read paths feed it. */
export interface CartLineAmountFields {
  finalItemPrice?: number | null
  price?: number | null
  quantity?: number | null
}

/**
 * The money actually charged for one cart line.
 *
 * `finalItemPrice` when the line carries a usable one — including **0**, which is a real value (a
 * fully-discounted or complimentary line) and must not fall through to a list price. Otherwise the
 * unit price multiplied back up by the quantity, which is the only way a unit price can be added to
 * a line-total accumulator without changing what the accumulator means.
 */
export function chargedLineAmount(item: CartLineAmountFields | null | undefined): number {
  if (!item) return 0
  if (typeof item.finalItemPrice === 'number' && Number.isFinite(item.finalItemPrice)) {
    return item.finalItemPrice
  }
  const unit = typeof item.price === 'number' && Number.isFinite(item.price) ? item.price : 0
  const quantity =
    typeof item.quantity === 'number' && Number.isFinite(item.quantity) ? item.quantity : 0
  return unit * quantity
}
